import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useParams } from "react-router-dom";
import { productsApi } from "../../../../api/products";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import {
  isSectionAnchorTarget,
  manualDestinationLinkError,
  normalizeEditableColor,
  registryFieldError,
} from "./inspectorModel";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { VisualColorControl } from "./VisualSettingControls";

function ButtonDestinationEditor({ field, value, options, disabled, onCommit, defaultLabelPath = "", defaultLabelValue = "" }) {
  const current = String(value || "").trim();
  const currentIsSection = isSectionAnchorTarget(current);
  const selectedSection = currentIsSection && options.some((option) => option.value === current)
    ? current
    : "";
  const [linkDraft, setLinkDraft] = React.useState(currentIsSection ? "" : current);
  const [linkError, setLinkError] = React.useState("");
  // Se usa únicamente en las secciones libres; los demás botones conservan su editor anterior.
  const isCustomCta = /\.data\.custom\.ctaUrl$/.test(String(field?.path || ""));
  const { projectId } = useParams();
  const [productSearch, setProductSearch] = React.useState("");
  const [catalogItems, setCatalogItems] = React.useState([]);
  const [chosenProduct, setChosenProduct] = React.useState(null);
  const [catalogLoading, setCatalogLoading] = React.useState(false);
  const [catalogError, setCatalogError] = React.useState("");
  React.useEffect(() => {
    if (!isCustomCta || !projectId) return undefined;
    let alive = true;
    const timer = setTimeout(async () => {
      setCatalogLoading(true);
      setCatalogError("");
      try {
        const response = await productsApi.list(projectId, {
          page: 1,
          pageSize: 100,
          ...(productSearch.trim() ? { q: productSearch.trim() } : {}),
        });
        if (!alive) return;
        const results = Array.isArray(response?.products) ? response.products
          : Array.isArray(response?.items) ? response.items : [];
        setCatalogItems(results.filter((item) => item && (item._id || item.id)));
      } catch (_error) {
        if (!alive) return;
        setCatalogError("No pudimos cargar los ítems. Podés seguir usando un enlace manual.");
        setCatalogItems([]);
      } finally {
        if (alive) setCatalogLoading(false);
      }
    }, productSearch ? 300 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [isCustomCta, projectId, productSearch]);

  const productHref = (item) => `/producto/${encodeURIComponent(String(item?._id || item?.id || ""))}`;
  const productName = (item) => String(item?.name || item?.nombre || item?.title || item?.displayName || item?.code || item?.codigo || item?._id || item?.id || "Ítem");
  const selectedProduct = catalogItems.find((item) => productHref(item) === current)
    || (chosenProduct && productHref(chosenProduct) === current ? chosenProduct : null);
  const chooseProduct = (item) => {
    if (!item) return;
    setChosenProduct(item);
    setProductSearch(productName(item));
    chooseSection(productHref(item));
    if (defaultLabelPath && !String(defaultLabelValue || "").trim()) {
      onCommit(defaultLabelPath, "Ver producto");
    }
  };

  React.useEffect(() => {
    setLinkDraft(currentIsSection ? "" : current);
    setLinkError("");
  }, [current, currentIsSection]);

  const commitLink = () => {
    const nextValue = String(linkDraft || "").trim();
    const error = manualDestinationLinkError(field, nextValue);
    setLinkError(error);
    if (error || nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  const chooseSection = (nextValue) => {
    setLinkError("");
    setLinkDraft("");
    if (nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  return (
    <Stack spacing={0.9}>
      <TextField
        fullWidth
        size="small"
        label="Link"
        value={linkDraft}
        disabled={disabled}
        error={Boolean(linkError)}
        inputProps={{ maxLength: field.maxLength }}
        placeholder="https://sitio.com, /ruta, mailto:…"
        helperText={linkError || "Para un sitio, una ruta, email o teléfono. Las secciones de esta landing se eligen abajo."}
        onChange={(event) => {
          setLinkDraft(event.target.value);
          if (linkError) setLinkError("");
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.nativeEvent?.isComposing) return;
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.target?.blur === "function") event.target.blur();
          else commitLink();
        }}
        onBlur={commitLink}
      />
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>O ir a una sección</InputLabel>
        <Select
          label="O ir a una sección"
          value={selectedSection}
          onChange={(event) => chooseSection(event.target.value)}
        >
          <MenuItem value=""><em>Sin sección</em></MenuItem>
          {options.map((option) => (
            <MenuItem key={`${field.path}:${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          Sólo aparecen secciones visibles que existen en esta landing. El ancla se asigna automáticamente.
        </FormHelperText>
      </FormControl>
      {isCustomCta ? (
        <Autocomplete
          fullWidth
          size="small"
          disabled={disabled || !projectId}
          options={catalogItems}
          value={selectedProduct}
          loading={catalogLoading}
          noOptionsText={catalogLoading ? "Buscando…" : "No hay ítems para esta búsqueda"}
          filterOptions={(items) => items}
          getOptionLabel={productName}
          isOptionEqualToValue={(option, selected) => String(option?._id || option?.id) === String(selected?._id || selected?.id)}
          inputValue={productSearch}
          onInputChange={(_event, next, reason) => { if (reason === "input" || reason === "clear") setProductSearch(next); }}
          onChange={(_event, item) => chooseProduct(item)}
          renderOption={(props, item) => (
            <li {...props} key={String(item._id || item.id)}>
              {productName(item)}{(item.code || item.codigo) ? ` · ${item.code || item.codigo}` : ""}{(item.active === false || item.activo === false) ? " · Oculto en catálogo" : ""}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="O seleccionar un ítem del catálogo"
              placeholder="Buscar por nombre o código"
              helperText={catalogError || "Al elegirlo se vincula al detalle /producto/ID. Buscá para encontrar otros ítems."}
            />
          )}
        />
      ) : null}
    </Stack>
  );
}

function RegisteredBehaviorTextField({ field, value, disabled, onCommit }) {
  const current = String(value ?? "");
  const [draft, setDraft] = React.useState(current);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setDraft(current);
    setError("");
  }, [current, field.path]);

  const commit = () => {
    const nextValue = String(draft || "").trim();
    const nextError = registryFieldError(field, nextValue);
    setError(nextError);
    if (nextError || nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={field.label}
      value={draft}
      disabled={disabled}
      error={Boolean(error)}
      inputProps={{ maxLength: field.maxLength }}
      placeholder={field.validator === "calendly" ? "https://calendly.com/tu-negocio/turno" : undefined}
      helperText={error || field.helperText || "Se guarda al salir del campo."}
      onChange={(event) => {
        setDraft(event.target.value);
        if (error) setError("");
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" || event.nativeEvent?.isComposing) return;
        event.preventDefault();
        commit();
      }}
      onBlur={commit}
    />
  );
}
function NavbarButtonsEditor({ property, items, options = [], form, disabled, onCommit, onCommitMany, showIndividualColors, getElementColor, getElementFallback, onElementColorChange, onElementColorReset }) {
  const safeItems = Array.isArray(items) ? items : [];
  const maxItems = Number.isFinite(property?.limits?.maxItems) ? property.limits.maxItems : 8;
  const reachedLimit = safeItems.length >= maxItems;
  const exceededLimit = safeItems.length > maxItems;
  const limitFeedbackId = `${String(property?.path || "navigation-buttons").replace(/[^a-z0-9_-]/gi, "-")}-limit-feedback`;
  const [errors, setErrors] = React.useState({});
  const [confirmClear, setConfirmClear] = React.useState(false);
  const builtInLinks = [
    ["navigation.showHomeLink", "Inicio"],
    ["navigation.showProductsLink", "Productos / Carta"],
    ["navigation.showAboutLink", "Sobre nosotros"],
    ["navigation.showHighlightsLink", "Diferenciales"],
    ["navigation.showContactLink", "Contacto"],
    ["navigation.showCustomerAccess", "Acceso clientes"],
    ["navigation.showCart", "Carrito"],
  ];
  const toggles = form?.navigation || {};
  const builtInCount = builtInLinks.filter(([path]) => toggles[path.split(".")[1]] !== false).length;
  const applyGroup = (changes) => {
    if (typeof onCommitMany === "function") onCommitMany(changes);
    else Object.entries(changes).forEach(([path, value]) => onCommit(path, value));
  };
  const clearNavbar = () => {
    applyGroup({
      "navigation.buttons": [],
      ...Object.fromEntries(builtInLinks.map(([path]) => [path, false])),
    });
    setErrors({});
    setConfirmClear(false);
  };
  const restoreBuiltIns = () => applyGroup(Object.fromEntries(builtInLinks.map(([path]) => [path, true])));

  const updateButton = (index, patch) => {
    const nextItems = [...safeItems];
    nextItems[index] = { ...(nextItems[index] || {}), ...patch };
    onCommit(property.path, nextItems);
  };

  const addButton = () => {
    if (safeItems.length >= maxItems) return;
    onCommit(property.path, [
      ...safeItems,
      {
        id: `nav-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        label: "",
        behavior: "section",
        target: "",
        megaMenuContent: "inherit",
        megaMenuDisplay: "inherit",
        megaMenuHierarchy: "inherit",
      },
    ]);
  };

  const moveButton = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= safeItems.length) return;
    const next = [...safeItems];
    [next[index], next[target]] = [next[target], next[index]];
    onCommit(property.path, next);
  };

  const removeButton = (index) => {
    setErrors((current) => {
      const next = { ...current };
      delete next[index];
      return next;
    });
    onCommit(property.path, safeItems.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2.5,
        bgcolor: "#F7F8FA",
        border: "1px solid rgba(25,36,47,.08)",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: reachedLimit ? 0.65 : 1, flexWrap: "wrap", rowGap: 0.85 }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 190px" }}>
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.4 }}>
            <Typography sx={{ color: "#19242F", fontWeight: 900 }}>
              Botones de la navbar
            </Typography>
            <Typography
              component="span"
              variant="caption"
              aria-label={`${safeItems.length} de ${maxItems} botones configurados`}
              sx={{
                px: 0.8,
                py: 0.2,
                borderRadius: 999,
                color: reachedLimit ? "#8A5A00" : "rgba(25,36,47,.58)",
                bgcolor: reachedLimit ? "rgba(237,164,0,.12)" : "rgba(25,36,47,.06)",
                fontWeight: 900,
                whiteSpace: "nowrap",
              }}
            >
              {safeItems.length} / {maxItems}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
            Agregá botones libres. Cada botón conserva un ID y estilos propios aunque lo reordenes; seleccioná el botón en el canvas para editar su apariencia.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || reachedLimit}
          aria-describedby={reachedLimit ? limitFeedbackId : undefined}
          onClick={addButton}
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, flexShrink: 0 }}
        >
          Agregar botón
        </Button>
      </Stack>

      <Box sx={{ mb: 1.3, p: 1.1, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.12)", borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 850, mb: 0.3 }}>Accesos predeterminados</Typography>
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
          Activá sólo los accesos que necesitás. Ocultarlos no elimina sus textos ni destinos guardados.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 0.2 }}>
          {builtInLinks.map(([path, label]) => (
            <FormControlLabel
              key={path}
              sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 650 } }}
              control={<Switch size="small" checked={toggles[path.split(".")[1]] !== false} disabled={disabled} onChange={(event) => onCommit(path, event.target.checked)} />}
              label={label}
            />
          ))}
        </Box>
        <Stack direction="row" flexWrap="wrap" gap={0.8} sx={{ mt: 1 }}>
          <Button size="small" variant="outlined" disabled={disabled || builtInCount === builtInLinks.length} onClick={restoreBuiltIns} sx={{ textTransform: "none" }}>Mostrar predeterminados</Button>
          {!confirmClear ? (
            <Button size="small" color="error" variant="outlined" disabled={disabled || (!safeItems.length && !builtInCount)} onClick={() => setConfirmClear(true)} sx={{ textTransform: "none" }}>
              Limpiar toda la navbar
            </Button>
          ) : (
            <>
              <Button size="small" color="error" variant="contained" disabled={disabled} onClick={clearNavbar} sx={{ textTransform: "none" }}>Confirmar limpieza</Button>
              <Button size="small" onClick={() => setConfirmClear(false)} sx={{ textTransform: "none" }}>Cancelar</Button>
            </>
          )}
        </Stack>
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: "text.secondary" }}>
          Limpiar oculta Inicio, los enlaces de sistema, acceso y carrito, y borra los botones personalizados. El logo queda visible; podés deshacer antes de guardar.
        </Typography>
      </Box>

      {reachedLimit ? (
        <Typography
          id={limitFeedbackId}
          role="status"
          variant="caption"
          sx={{ display: "block", mb: 1, color: "#8A5A00", fontWeight: 750 }}
        >
          {exceededLimit
            ? `Hay ${safeItems.length} botones y el máximo actual es ${maxItems}. Eliminá los que sobren antes de agregar otro.`
            : `Alcanzaste el máximo de ${maxItems} botones. Eliminá uno para agregar otro.`}
        </Typography>
      ) : null}

      {!safeItems.length ? (
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
          Todavía no agregaste botones personalizados a la navbar.
        </Typography>
      ) : null}

      <Stack spacing={1.15}>
        {safeItems.map((item, index) => {
          const label = String(item?.label || "");
          const behavior = ["section", "link", "mega-menu"].includes(item?.behavior)
            ? item.behavior
            : "section";
          const target = String(item?.target || "").trim();
          const selectedSection = behavior === "section" && options.some((option) => option.value === target)
            ? target
            : "";
          const linkError = errors[index] || "";

          return (
            <Box key={item?.id || `${property.path}:${index}`} sx={{ p: 1.15, borderRadius: 2.25, bgcolor: "#fff" }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.9 }}>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)", fontWeight: 850 }}>
                  Botón {index + 1}
                </Typography>
                <Stack direction="row" spacing={0.3}>
                <Button size="small" disabled={disabled || index === 0} onClick={() => moveButton(index, -1)} sx={{ minWidth: 0, px: 0.6 }}>↑</Button>
                <Button size="small" disabled={disabled || index === safeItems.length - 1} onClick={() => moveButton(index, 1)} sx={{ minWidth: 0, px: 0.6 }}>↓</Button>
                <Button
                  size="small"
                  color="error"
                  disabled={disabled}
                  onClick={() => removeButton(index)}
                  sx={{ minWidth: 0, px: 1, textTransform: "none", fontWeight: 800 }}
                >
                  Quitar
                </Button>
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <TextField
                  key={`${item?.id || index}:label:${label}`}
                  fullWidth
                  size="small"
                  label="Texto del botón"
                  defaultValue={label}
                  disabled={disabled}
                  inputProps={{ maxLength: 80 }}
                  helperText="Este es el texto que se verá en la navbar."
                  onBlur={(event) => {
                    const nextValue = event.target.value.trim();
                    if (nextValue !== label) updateButton(index, { label: nextValue });
                  }}
                />

                <FormControl fullWidth size="small" disabled={disabled}>
                  <InputLabel>Comportamiento</InputLabel>
                  <Select
                    label="Comportamiento"
                    value={behavior}
                    onChange={(event) => {
                      setErrors((current) => ({ ...current, [index]: "" }));
                      updateButton(index, { behavior: event.target.value, target: "" });
                    }}
                  >
                    <MenuItem value="section">Deslizar a una sección</MenuItem>
                    <MenuItem value="link">Abrir un link</MenuItem>
                    <MenuItem value="mega-menu">Desplegar mega menú</MenuItem>
                  </Select>
                </FormControl>

                {behavior === "section" ? (
                  <FormControl fullWidth size="small" disabled={disabled || !options.length}>
                    <InputLabel>Sección de destino</InputLabel>
                    <Select
                      label="Sección de destino"
                      value={selectedSection}
                      onChange={(event) => updateButton(index, { target: event.target.value })}
                    >
                      <MenuItem value=""><em>Elegí una sección</em></MenuItem>
                      {options.map((option) => (
                        <MenuItem key={`${item?.id || index}:${option.value}`} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {options.length
                        ? "Sólo aparecen secciones existentes y habilitadas. No se pueden escribir anclas manualmente."
                        : "No hay secciones habilitadas disponibles como destino."}
                    </FormHelperText>
                  </FormControl>
                ) : behavior === "link" ? (
                  <TextField
                    key={`${item?.id || index}:target:${target}`}
                    fullWidth
                    size="small"
                    label="Link"
                    defaultValue={target}
                    disabled={disabled}
                    error={Boolean(linkError)}
                    inputProps={{ maxLength: 2000 }}
                    placeholder="https://sitio.com o /ruta"
                    helperText={linkError || "Podés usar un sitio, una ruta, email o teléfono. Para secciones usá el comportamiento Deslizar."}
                    onBlur={(event) => {
                      const nextValue = event.target.value.trim();
                      const error = manualDestinationLinkError(
                        { type: SEGMENT_FIELD_TYPES.URL, maxLength: 2000 },
                        nextValue
                      );
                      setErrors((current) => ({ ...current, [index]: error }));
                      if (!error && nextValue !== target) updateButton(index, { target: nextValue });
                    }}
                  />
                ) : (
                  <Stack spacing={1}>
                    <FormControl fullWidth size="small" disabled={disabled}>
                      <InputLabel>Contenido del mega menú</InputLabel>
                      <Select
                        label="Contenido del mega menú"
                        value={item?.megaMenuContent || "inherit"}
                        onChange={(event) => updateButton(index, { megaMenuContent: event.target.value })}
                      >
                        <MenuItem value="inherit">Usar configuración general</MenuItem>
                        <MenuItem value="auto">Automático</MenuItem>
                        <MenuItem value="products">Sólo productos</MenuItem>
                        <MenuItem value="services">Sólo servicios</MenuItem>
                        <MenuItem value="mixed">Productos y servicios</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" disabled={disabled}>
                      <InputLabel>Qué mostrar</InputLabel>
                      <Select
                        label="Qué mostrar"
                        value={item?.megaMenuDisplay || "inherit"}
                        onChange={(event) => updateButton(index, { megaMenuDisplay: event.target.value })}
                      >
                        <MenuItem value="inherit">Usar configuración general</MenuItem>
                        <MenuItem value="categories">Sólo categorías</MenuItem>
                        <MenuItem value="items">Sólo ítems</MenuItem>
                        <MenuItem value="both">Categorías + ítems</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" disabled={disabled}>
                      <InputLabel>Jerarquía</InputLabel>
                      <Select
                        label="Jerarquía"
                        value={item?.megaMenuHierarchy || "inherit"}
                        onChange={(event) => updateButton(index, { megaMenuHierarchy: event.target.value })}
                      >
                        <MenuItem value="inherit">Usar configuración general</MenuItem>
                        <MenuItem value="category-first">Categoría → subcategoría</MenuItem>
                        <MenuItem value="subcategory-first">Subcategoría → categoría</MenuItem>
                      </Select>
                    </FormControl>
                    <FormHelperText>Este botón abre el catálogo desplegable sin navegar a otra ruta.</FormHelperText>
                  </Stack>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
function IndividualTextColorControl({
  fieldPath,
  label,
  value,
  fallback,
  disabled,
  onChange,
  onReset,
}) {
  return (
    <VisualColorControl
      key={fieldPath}
      label={`Color individual · ${label}`}
      value={value}
      fallback={fallback}
      disabled={disabled}
      helperText={value ? "Color propio de este texto." : "Heredado de la paleta de esta sección."}
      copyHex
      onChange={onChange}
      onReset={onReset}
    />
  );
}

function LevelSelector({ label, value, options, onChange }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        aria-label={label}
        value={value}
        onChange={(_, next) => next && onChange(next)}
      >
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none", fontWeight: 850 }}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

function InspectorAccordion({
  id,
  expanded,
  onChange,
  icon: Icon,
  title,
  description,
  children,
}) {
  return (
    <Accordion
      expanded={expanded === id}
      onChange={(_, nextExpanded) => onChange(nextExpanded ? id : false)}
      disableGutters
      elevation={0}
      sx={{
        border: "1px solid rgba(25,36,47,.09)",
        borderRadius: "14px !important",
        bgcolor: "#fff",
        overflow: "hidden",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        aria-controls={`segment-inspector-${id}-content`}
        id={`segment-inspector-${id}-header`}
        sx={{
          px: 1.4,
          minHeight: 58,
          "& .MuiAccordionSummary-content": { my: 1 },
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(243,90,58,.10)",
              color: "#F35A3A",
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#19242F", fontWeight: 950, lineHeight: 1.15 }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)", lineHeight: 1.3 }}>
              {description}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        id={`segment-inspector-${id}-content`}
        sx={{ px: 1.4, pt: 0.5, pb: 1.5 }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export {
  ButtonDestinationEditor,
  RegisteredBehaviorTextField,
  NavbarButtonsEditor,
  IndividualTextColorControl,
  LevelSelector,
  InspectorAccordion
};
